import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const PaymentCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("global");
  
  // State to control the content rendered on the page
  const [paymentStatus, setPaymentStatus] = useState("processing"); // 'processing', 'success', 'failed', 'cancelled', 'unknown'

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    // PayTabs به return URL پارامتر respStatus را ارسال می‌کند (A, D, C)
    const respStatus = query.get("respStatus"); 
    // بک‌اند شما پس از پردازش کال‌بک PayTabs، می‌تواند پارامتر status (success, failed) را به فرانت‌اند ارسال کند
    const backendStatus = query.get("status"); 
    const orderId = query.get("orderId");

    let timer;

    // اولویت با status ارسالی از بک‌اند (اگر PayTabs کال‌بک سرور به سرور را ارسال کرده باشد)
    if (backendStatus === "success") {
      setPaymentStatus("success");
      toast.success(t("payment.successToast"));
      localStorage.removeItem("pendingOrder");
      timer = setTimeout(() => {
        navigate(`/order-success${orderId ? `?orderId=${orderId}` : ''}`);
      }, 5000); // Redirect after 5 seconds
    } else if (backendStatus === "failed") {
      setPaymentStatus("failed");
      toast.error(t("payment.failedToast"));
      timer = setTimeout(() => {
        navigate("/checkout");
      }, 5000); // Redirect after 5 seconds
    } 
    // اگر بک‌اند status مشخصی ارسال نکرده، respStatus از PayTabs را بررسی می‌کنیم (برای لغو مستقیم توسط کاربر)
    else if (respStatus === "C") { // C = Cancelled (از PayTabs)
      setPaymentStatus("cancelled");
      toast.warn(t("payment.cancelledToast"));
      timer = setTimeout(() => {
        navigate("/checkout"); // هدایت به صفحه پرداخت برای تلاش مجدد
      }, 5000); // Redirect after 5 seconds
    } else if (respStatus === "D") { // D = Declined (از PayTabs)
      setPaymentStatus("failed"); // درگاه پرداخت آن را رد کرده
      toast.error(t("payment.failedToast"));
      timer = setTimeout(() => {
        navigate("/checkout");
      }, 5000); // Redirect after 5 seconds
    }
    else if (respStatus === "A") { // A = Authorized (از PayTabs) - اگر بک‌اند به هر دلیلی status=success را نفرستاده باشد
      setPaymentStatus("success");
      toast.success(t("payment.successToast"));
      localStorage.removeItem("pendingOrder");
      timer = setTimeout(() => {
        navigate(`/order-success${orderId ? `?orderId=${orderId}` : ''}`);
      }, 5000); // Redirect after 5 seconds
    }
    else {
      // وضعیت پیش‌فرض: نه از بک‌اند status داریم، نه از PayTabs پارامتر respStatus
      // این حالت "نامشخص" است.
      setPaymentStatus("unknown");
      toast.info(t("payment.unknownToast"));
      // در این حالت، دیگر به صورت خودکار هدایت نمی‌کنیم، بلکه دکمه‌هایی برای کاربر نمایش می‌دهیم.
      // بنابراین نیازی به timer در این بلاک نیست.
    }
    
    // Cleanup function to clear the timer
    return () => clearTimeout(timer);
  }, [location, navigate, t]);

  // Conditional rendering based on payment status
  const renderContent = () => {
    if (paymentStatus === "processing") {
      return (
        <>
          <p className="text-xl font-semibold text-gray-800">{t("payment.processingTitle")}</p>
          <p className="text-gray-600 mt-2">{t("payment.processingMessage")}</p>
          <div className="mt-4 flex justify-center">
            <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        </>
      );
    } else if (paymentStatus === "success") {
      return (
        <>
          <div className="flex justify-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-green-500" viewBox="0 0 24 24" fill="currentColor">
              <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.532-2.196-1.637a.75.75 0 00-.926.978l3.296 2.45c.164.122.38.195.602.195.222 0 .44-.073.602-.195l4.316-6.046z" clipRule="evenodd" />
            </svg>
          </div>
          <p className="text-xl font-semibold text-green-700">{t("payment.successTitle")}</p>
          <p className="text-gray-600 mt-2">{t("payment.successMessage")}</p>
        </>
      );
    } else if (paymentStatus === "failed") {
      return (
        <>
          <div className="flex justify-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-red-500" viewBox="0 0 24 24" fill="currentColor">
              <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-2.72 2.72a.75.75 0 101.06 1.06L12 13.06l2.72 2.72a.75.75 0 101.06-1.06L13.06 12l2.72-2.72a.75.75 0 00-1.06-1.06L12 10.94l-2.72-2.72z" clipRule="evenodd" />
            </svg>
          </div>
          <p className="text-xl font-semibold text-red-700">{t("payment.failedTitle")}</p>
          <p className="text-gray-600 mt-2">{t("payment.failedMessage")}</p>
        </>
      );
    } else if (paymentStatus === "cancelled") {
      return (
        <>
          <div className="flex justify-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-yellow-500" viewBox="0 0 24 24" fill="currentColor">
              <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM12.75 9a.75.75 0 00-1.5 0v2.25H9a.75.75 0 000 1.5h2.25V15a.75.75 0 001.5 0v-2.25H15a.75.75 0 000-1.5h-2.25V9z" clipRule="evenodd" />
            </svg>
          </div>
          <p className="text-xl font-semibold text-yellow-700">{t("payment.cancelledTitle")}</p>
          <p className="text-gray-600 mt-2">{t("payment.cancelledMessage")}</p>
        </>
      );
    } else if (paymentStatus === "unknown") {
      return (
        <>
          <div className="flex justify-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-500" viewBox="0 0 24 24" fill="currentColor">
              <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.198 0 .693 1.2 1.902 1.955 3.057 2.284 1.25.353 2.377 1.062 2.972 2.188.58 1.118.58 2.518 0 3.636-.595 1.125-1.722 1.834-2.972 2.188-1.155.329-2.364 1.084-3.057 2.283-1.155 2-4.043 2-5.198 0-.693-1.2-1.902-1.955-3.057-2.284-1.25-.353-2.377-1.062-2.972-2.188-.58-1.118-.58-2.518 0-3.636.595-1.125 1.722-1.834 2.972-2.188 1.155-.329 2.364-1.084 3.057-2.283zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zM12 15a.75.75 0 100 1.5.75.75 0 000-1.5z" clipRule="evenodd" />
            </svg>
          </div>
          <p className="text-xl font-semibold text-gray-700">{t("payment.unknownTitle")}</p>
          <p className="text-gray-600 mt-2">{t("payment.unknownMessage")}</p>
          <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
            <button
              onClick={() => navigate("/")}
              className="px-6 py-3 rounded-md bg-blue-600 text-white font-medium hover:bg-blue-700 transition duration-200"
            >
              {t("payment.goToHomeButton")}
            </button>
            <button
              onClick={() => navigate("/checkout")}
              className="px-6 py-3 rounded-md border border-blue-600 text-blue-600 font-medium hover:bg-blue-50 transition duration-200"
            >
              {t("payment.tryAgainButton")}
            </button>
          </div>
        </>
      );
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="bg-white p-8 rounded-lg shadow-lg text-center max-w-md w-full">
        {renderContent()}
      </div>
    </div>
  );
};

export default PaymentCallback;
